import { useCallback } from "react";
import { AccrualImporterRow } from "../types";
import { propagateSharedField } from "../useAccrualImporterState";
import {
    applySpreadsheetColumnUpdate,
    isAccrualImporterRowKey,
} from "./applySpreadsheetColumnUpdate";

export function useAccrualImporterHeaderFieldUpdate({
    setRows,
    globalRowIndex,
}: {
    setRows: React.Dispatch<React.SetStateAction<AccrualImporterRow[]>>;
    globalRowIndex: number;
}) {
    const updateField = useCallback(
        (
            field: keyof AccrualImporterRow,
            value: AccrualImporterRow[keyof AccrualImporterRow],
        ) => {
            if (!isAccrualImporterRowKey(field)) {
                return;
            }

            setRows((prevRows) => {
                const nextRows = [...prevRows];
                const currentRow = nextRows[globalRowIndex];

                if (!currentRow) {
                    return prevRows;
                }

                nextRows[globalRowIndex] = applySpreadsheetColumnUpdate({
                    row: currentRow,
                    columnField: field,
                    incoming: { [field]: value },
                });

                propagateSharedField({
                    rows: nextRows,
                    prevRows,
                    rowIndex: globalRowIndex,
                    field,
                });

                return nextRows;
            });
        },
        [globalRowIndex, setRows],
    );

    const updateCounterparty = useCallback(
        (counterparty: { id: string; name?: string } | undefined) => {
            setRows((prevRows) => {
                const nextRows = [...prevRows];
                const currentRow = nextRows[globalRowIndex];

                if (!currentRow) {
                    return prevRows;
                }

                const counterpartyId = counterparty?.id ?? null;
                const counterpartyName = counterparty?.name ?? null;

                nextRows[globalRowIndex] = applySpreadsheetColumnUpdate({
                    row: applySpreadsheetColumnUpdate({
                        row: currentRow,
                        columnField: "counterpartyId",
                        incoming: { counterpartyId },
                    }),
                    columnField: "counterpartyName",
                    incoming: { counterpartyName },
                });

                propagateSharedField({
                    rows: nextRows,
                    prevRows,
                    rowIndex: globalRowIndex,
                    field: "counterpartyId",
                });
                propagateSharedField({
                    rows: nextRows,
                    prevRows,
                    rowIndex: globalRowIndex,
                    field: "counterpartyName",
                });

                return nextRows;
            });
        },
        [globalRowIndex, setRows],
    );

    return { updateField, updateCounterparty };
}
